<script setup>
import { isNil, isNumber } from 'lodash'
import { ref, useTemplateRef, computed, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import DataSourcesApiHandler from '@/api/datasources'
import {
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  BUTTON_ICON_DENSITIES
} from '@/utils/components-configurations/app-button'
import { DIALOG_DENSITY } from '@/utils/components-configurations/app-dialog'
import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import {
  DATA_SOURCE_QUERY_NAME_MAX_LENGTH,
  DATA_SOURCE_SETUP_GUIDE_LINK,
  DATA_SOURCE_TYPES
} from '@/utils/datasources'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { KEYWORD } from '@/utils/helpers'

import AppDialog from '@/components/AppDialog'
import AppHrefLink from '@/components/AppHrefLink'
import ElementDataSourceIcon from '@/components/form/ElementDataSourceIcon'
import FormFieldNext from '@/components/form/FormFieldNext'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppDeleteModal from '@/components/ui/AppDeleteModal/AppDeleteModal'
import AppDivider from '@/components/ui/AppDivider'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import AppTextarea from '@/components/ui/AppTextarea/AppTextarea'

defineOptions({
  name: 'PowerBIQueryModal'
})

const props = defineProps({
  elementId: {
    type: [String, Number],
    required: true
  },

  dataSourceQuery: {
    type: Object,
    default: () => null
  }
})

const emit = defineEmits({
  'delete-data-source': null,
  'add-data-source': null,
  'update-element': null
})

const { t } = useI18n()
const store = useStore()

const ADD_DATA_SOURCE_ACTION = 'add-account'
const DATA_SOURCE_QUERY_ERROR_CODE = 1

const isOpen = defineModel('show', {
  type: Boolean,
  required: true
})

const dataSource = useTemplateRef('dataSource')

const userDataSources = computed(() => store.state.system.userData?.userDataSources ?? [])
const dataSourcesOptions = computed(() => [
  ...userDataSources.value,
  { accountName: t('action.add_data_source'), id: ADD_DATA_SOURCE_ACTION }
])
const dataSourceBottomFixedItems = [ADD_DATA_SOURCE_ACTION]
const isAddDataSourceFixedItem = option => option.id === ADD_DATA_SOURCE_ACTION

const defaultDataSourceQueryFormModel = () => {
  return {
    dataSourceId: null,
    datasetId: null,
    displayName: '',
    query: ''
  }
}

const dataSourceQueryFormModel = ref(defaultDataSourceQueryFormModel())

const onAfterEnter = () => {
  if (isEdit.value) {
    updateDataSourceQueryFormForEdit(props.dataSourceQuery)
  } else {
    updateDataSourceQueryFormForAddition(props.dataSourceQuery)
  }
}

watch(
  () => isOpen.value,
  newValue => {
    if (!newValue) {
      clearState()
    }
  }
)

const isEdit = computed(() => {
  return !isNil(props.dataSourceQuery?.dataSourceQueryId)
})

const canEdit = computed(() => {
  if (!isEdit.value) return true

  return dataSourceQueryFormModel.value.isDataSourceOwner
})

const isQueryLoading = ref(false)

const updateDataSourceQueryFormForEdit = async dataSourceQuery => {
  const dataSourcesApi = new DataSourcesApiHandler()

  try {
    isQueryLoading.value = true
    const query = await dataSourcesApi.getQueryById({
      dataSourceId: dataSourceQuery.dataSourceId,
      dataSourceQueryId: dataSourceQuery.dataSourceQueryId
    })
    dataSourceQueryFormModel.value = {
      dataSourceId: query.dataSourceId,
      datasetId: query.params.datasetId,
      displayName: query.dataSourceQueryName,
      query: query.params.query,
      isDataSourceOwner: query.isDataSourceOwner
    }

    await loadDatasets({ requiredId: query.params.datasetId })
  } catch (error) {
    handleError({ error })
  } finally {
    isQueryLoading.value = false
  }
}

const updateDataSourceQueryFormForAddition = async dataSourceQuery => {
  dataSourceQueryFormModel.value = {
    dataSourceId: dataSourceQuery.dataSourceId,
    datasetId: null,
    displayName: '',
    query: ''
  }

  await loadDatasets()
}

const dataSourceQueryError = ref(false)

const isSaveDisabled = computed(() => {
  return (
    isNil(dataSourceQueryFormModel.value.dataSourceId) ||
    isNil(dataSourceQueryFormModel.value.datasetId) ||
    isStringEmpty(dataSourceQueryFormModel.value.displayName) ||
    isStringEmpty(dataSourceQueryFormModel.value.query)
  )
})

const datasets = ref([])

const isDatasetsLoading = ref(false)
const loadDatasets = async ({ requiredId = null } = {}) => {
  isDatasetsLoading.value = true
  datasets.value = await fetchDatasets({ requiredId })
  isDatasetsLoading.value = false
}

const fetchDatasets = async ({ requiredId = null, searchString = null } = {}) => {
  const dataSourcesApi = new DataSourcesApiHandler()

  try {
    const data = await dataSourcesApi.getDatasets({
      dataSourceId: dataSourceQueryFormModel.value.dataSourceId,
      requiredId,
      searchString
    })
    return data.datasets
  } catch (error) {
    handleError({ error })
    return []
  }
}

const onSelectDataSource = id => {
  if (isQueryLoading.value) return

  if (id === ADD_DATA_SOURCE_ACTION) {
    emit('add-data-source')
  } else {
    dataSourceQueryFormModel.value.dataSourceId = id

    datasets.value = []
    dataSourceQueryFormModel.value.datasetId = null
    loadDatasets()
  }
}

const closeButtonText = computed(() => {
  return canEdit.value ? t('action.cancel') : t('action.close')
})

const onClose = () => {
  isOpen.value = false
}

const isSaveLoading = ref(false)
const onSave = async () => {
  const dataSourcesApi = new DataSourcesApiHandler()

  dataSourceQueryError.value = false

  try {
    isSaveLoading.value = true
    if (isEdit.value) {
      const payload = {
        dataSourceId: props.dataSourceQuery.dataSourceId,
        dataSourceQueryId: props.dataSourceQuery.dataSourceQueryId,
        displayName: dataSourceQueryFormModel.value.displayName,
        params: {
          datasetId: dataSourceQueryFormModel.value.datasetId,
          query: dataSourceQueryFormModel.value.query
        }
      }
      await dataSourcesApi.updateQuery(payload)
    } else {
      const payload = {
        dataSourceId: dataSourceQueryFormModel.value.dataSourceId,
        elementId: props.elementId,
        displayName: dataSourceQueryFormModel.value.displayName,
        params: {
          datasetId: dataSourceQueryFormModel.value.datasetId,
          query: dataSourceQueryFormModel.value.query
        }
      }
      await dataSourcesApi.createQuery(payload)
    }
    emit('update-element')
    onClose()
  } catch (error) {
    const errorCode = error.response?.data?.errorCode
    if (isNumber(errorCode) && errorCode === DATA_SOURCE_QUERY_ERROR_CODE) {
      dataSourceQueryError.value = true
    } else {
      handleError({ error })
    }
  } finally {
    isSaveLoading.value = false
  }
}

const clearState = () => {
  nextTick(() => {
    datasets.value = []

    dataSourceQueryFormModel.value = defaultDataSourceQueryFormModel()
    dataSourceQueryError.value = false

    isQueryLoading.value = false
    isDatasetsLoading.value = false
    isSaveLoading.value = false

    resetDeleteDataSource()
  })
}

//#region Delete Data Source
const deleteDataSourceOpen = ref(false)
const deleteDataSourceLoading = ref(false)
const deleteDataSourceData = ref(null)

const onDeleteDataSource = data => {
  deleteDataSourceOpen.value = true
  deleteDataSourceData.value = data
  dataSource.value.hideDropdown()
}

const onConfirmDeleteDataSource = async () => {
  const dataSourcesApi = new DataSourcesApiHandler()

  deleteDataSourceLoading.value = true
  try {
    await dataSourcesApi.deleteDataSource({
      dataSourceId: deleteDataSourceData.value.id
    })
    await store.dispatch('system/fetchUserDataSources')

    dataSourceQueryFormModel.value.dataSourceId = null
    dataSourceQueryFormModel.value.datasetId = null
    datasets.value = []

    onCloseDeleteDataSource()
  } finally {
    deleteDataSourceLoading.value = false
  }
}

const onCloseDeleteDataSource = () => {
  deleteDataSourceOpen.value = false
  deleteDataSourceData.value = null
}

const resetDeleteDataSource = () => {
  deleteDataSourceOpen.value = false
  deleteDataSourceLoading.value = false
  deleteDataSourceData.value = null
}
//#endregion
</script>

<template>
  <AppDialog
    :density="DIALOG_DENSITY.COMPACT"
    :hide-confirm-button="!canEdit"
    :show="isOpen"
    :title="t('data_source.power_bi.title')"
    @on-close="onClose"
    @after-enter="onAfterEnter"
  >
    <template #title-icon>
      <ElementDataSourceIcon :type-id="DATA_SOURCE_TYPES.POWER_BI" />
    </template>
    <div class="pqm-Container">
      <FormFieldNext :label="t('data_source.power_bi.accounts')" required>
        <AppSelect
          ref="dataSource"
          :bottom-fixed-items="dataSourceBottomFixedItems"
          :disabled="isEdit"
          :model-value="dataSourceQueryFormModel.dataSourceId"
          :offset="[0, 0]"
          :options="dataSourcesOptions"
          :type="SELECT_TYPES.MODERN"
          item-label="accountName"
          item-value="id"
          split-bottom-fixed-items
          @update:model-value="onSelectDataSource"
        >
          <template #option-label="{ option }">
            <div
              v-if="option && !isAddDataSourceFixedItem(option)"
              class="pqm-Container_DataSourceOption"
            >
              <span class="oboard-truncated-text">
                {{ option.accountName }}
              </span>
              <AppButtonNext
                :icon-density="BUTTON_ICON_DENSITIES.FIT"
                :size="BUTTON_SIZES.XS"
                :variant="BUTTON_VARIANTS.LINK_GRAY"
                icon-before="delete-next"
                style="--abn-background: transparent"
                @click.stop="onDeleteDataSource(option)"
              />
            </div>
            <div
              v-if="option && isAddDataSourceFixedItem(option)"
              class="pqm-Container_DataSourceOption_BottomFixed"
            >
              <AppIcon height="24" icon-name="plus-next" width="24" />
              <div>
                {{ option.accountName }}
              </div>
            </div>
          </template>
        </AppSelect>
      </FormFieldNext>
      <AppDivider style="--divider-offset: 5px 0" />
      <FormFieldNext :label="t('data_source.power_bi.datasets')" required>
        <AppSelect
          v-model="dataSourceQueryFormModel.datasetId"
          :disabled="!canEdit"
          :loading="isDatasetsLoading"
          :offset="[0, -40]"
          :options="datasets"
          :search-function="searchString => fetchDatasets({ searchString })"
          :type="SELECT_TYPES.MODERN"
          dropdown-search
          item-label="name"
          item-value="id"
          skeleton-loader
          @update:options="datasets = $event"
        >
          <template #button-content="{ option }">
            <template v-if="!option">
              {{ t('data_source.power_bi.select_dataset_placeholder') }}
            </template>
          </template>
        </AppSelect>
      </FormFieldNext>
      <FormFieldNext :label="t('data_source.display_name')" required>
        <AppInput
          v-model="dataSourceQueryFormModel.displayName"
          :max-length="DATA_SOURCE_QUERY_NAME_MAX_LENGTH"
          :placeholder="t('data_source.display_name.placeholder')"
          :readonly="!canEdit"
          size="xlg"
        />
      </FormFieldNext>
      <FormFieldNext
        :label="t('data_source.power_bi.dax_query')"
        class="pqm_Container_Query"
        required
      >
        <AppTextarea
          v-model="dataSourceQueryFormModel.query"
          :disabled="!canEdit"
          :is-error="dataSourceQueryError"
          :placeholder="t('data_source.power_bi.dax_query.placeholder')"
          copyable
        >
          <template #error-text>
            <div>{{ $t('data_source.power_bi.dax_query.invalid_message') }}</div>
          </template>
        </AppTextarea>
      </FormFieldNext>
    </div>
    <template #footer-prepend>
      <AppHrefLink
        :href="DATA_SOURCE_SETUP_GUIDE_LINK"
        class="pqm-SetupGuide"
        prepend-icon="guide"
        prepend-icon-height="16"
        prepend-icon-width="16"
      >
        {{ $t('data_source.setup_guide') }}
      </AppHrefLink>
    </template>
    <template #button-cancel>
      <AppButtonNext :variant="BUTTON_VARIANTS.SECONDARY_GRAY" @click="onClose">
        {{ closeButtonText }}
      </AppButtonNext>
    </template>
    <template #button-confirm>
      <AppButtonNext
        v-if="canEdit"
        :disabled="isSaveDisabled"
        :loading="isSaveLoading"
        @click="onSave"
      >
        {{ $t('action.save') }}
      </AppButtonNext>
    </template>

    <portal to="modal-windows">
      <AppDeleteModal
        :keyword="KEYWORD"
        :loading="deleteDataSourceLoading"
        :show="deleteDataSourceOpen"
        :title="$t('data_source.delete_account')"
        keypath="data_source.delete_account.message"
        @on-confirm="onConfirmDeleteDataSource"
        @on-close="onCloseDeleteDataSource"
      >
        <template #i18n-t:accountName>
          {{ deleteDataSourceData?.accountName }}
        </template>
      </AppDeleteModal>
    </portal>
  </AppDialog>
</template>

<style lang="scss" scoped>
.pqm-Container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pqm-Container_DataSourceOption {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.pqm-Container_DataSourceOption_BottomFixed {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.pqm-SetupGuide {
  --gap: 4px;
  font-size: $fs-12;
  line-height: 20px;
}

.pqm_Container_Query {
  --ata-input-color: #{$primary-color-next};
  --ata-input-font-family: monospace;
  --ata-readonly-background-color: #{$grey-3-next};
  --ata-readonly-border-color: #{$grey-3-next};
}
</style>
